import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        // Same media list (e.g. a color change on the same product): keep the current gallery so the
        // already-loaded images aren't re-rendered, and just move to the slide the new render opens on.
        const newMediaIds = this.#getMediaIds(newMediaGallery);
        if (newMediaIds.length && newMediaIds.join(',') === this.#getMediaIds(this).join(',')) {
          const initialSlide = newMediaGallery.querySelector('slideshow-component')?.getAttribute('initial-slide');
          const mediaId = newMediaIds[Number.parseInt(initialSlide ?? '0', 10)];
          if (mediaId) this.selectMedia(mediaId);
          return;
        }

        this.replaceWith(newMediaGallery);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * Media ids of the gallery's main slides, in order.
   * @param {Element} gallery
   * @returns {string[]}
   */
  #getMediaIds(gallery) {
    return Array.from(gallery.querySelectorAll('slideshow-slide:not(zoom-dialog *) .product-media[data-media-id]')).map(
      (media) => media.getAttribute('data-media-id') ?? ''
    );
  }

  /**
   * Shows the slide containing the given media.
   * @param {string} mediaId
   */
  selectMedia(mediaId) {
    const { slideshow } = this;
    const slides = slideshow?.slides;
    if (!slideshow || !slides) return;

    const index = slides.findIndex((slide) => slide.querySelector(`.product-media[data-media-id="${CSS.escape(mediaId)}"]`));
    if (index === -1 || index === slideshow.current) return;

    slideshow.select(index, undefined, { animate: false });
  }

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
